/**
 * 灵感词 Hook
 *
 * @module utils/hooks/useInspirationWords
 */

import {
  OperationType,
  type DisplayedModule,
  type OperationRecord,
  type TriggerConfig,
  type TriggerResult,
} from "@/types/inspirationWords";
import { InspirationWordsService } from "@/utils/service/inspirationWords";
import { useCallback, useEffect, useRef, useState } from "react";

export interface UseInspirationWordsOptions {
  /** 页面范围标识（如 "discovery"），用于后端接口调用 */
  pageScope: string;
  /** 分类标识（可选，用于隔离同一页面下的不同分类，如 categoryId） */
  categoryKey?: string;
  /** 触发配置（可选，使用默认配置） */
  config?: TriggerConfig;
  /** 触发回调 */
  onTriggered?: (result: TriggerResult) => void;
  /** 是否启用（默认 true）。当为 false 时，Hook 仍会被调用但不执行任何逻辑 */
  enabled?: boolean;
}

export interface UseInspirationWordsReturn {
  /** 记录操作 */
  recordOperation: (fileId: string, type: OperationType) => Promise<void>;
  /** 已展示的推荐词模块 Map<fileId, DisplayedModule> */
  displayedModules: Map<string, DisplayedModule>;
  /** 清空记录 */
  clearRecords: () => void;
}

/**
 * 灵感词 Hook
 *
 * 功能：
 * 1. 记录用户操作（点击、采集、下载）
 * 2. 获取推荐词
 * 3. 检查触发条件
 * 4. 管理已展示的推荐词模块
 */
export function useInspirationWords({
  pageScope,
  categoryKey,
  config = InspirationWordsService.DEFAULT_CONFIG,
  onTriggered,
  enabled = true,
}: UseInspirationWordsOptions): UseInspirationWordsReturn {
  // 操作记录列表
  const [records, setRecords] = useState<OperationRecord[]>([]);
  // 上次触发时间
  const [lastTriggerTime, setLastTriggerTime] = useState<number>(0);
  // 已展示的推荐词模块 Map<fileId, DisplayedModule>
  const [displayedModules, setDisplayedModules] = useState<
    Map<string, DisplayedModule>
  >(new Map());

  // 组合的 scopeKey，用于检测变化
  const scopeKey = categoryKey ? `${pageScope}_${categoryKey}` : pageScope;
  const prevScopeKeyRef = useRef<string>(scopeKey);

  // 推荐词缓存：Map<`${fileId}_${type}`, WordMetadata[]>
  // 用于避免相同事件重复请求接口，不同事件类型可以返回不同的推荐词
  const wordsCacheRef = useRef<Map<string, any[]>>(new Map());

  // 当 categoryKey 变化时，清空所有数据，重新开始计算
  useEffect(() => {
    if (!enabled) return; // AB 未开启时跳过
    if (prevScopeKeyRef.current !== scopeKey) {
      prevScopeKeyRef.current = scopeKey;
      setRecords([]);
      setLastTriggerTime(0);
      setDisplayedModules(new Map());
      wordsCacheRef.current.clear(); // 清空缓存
    }
  }, [scopeKey, enabled]);

  // 清理定时器 ref
  const cleanupTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * 获取推荐词（带缓存，返回 WordMetadata[]）
   * @param fileId 文件ID（内容的唯一标识）
   * @param type 操作类型
   * @returns 推荐词元数据列表
   */
  const getRecommendWords = useCallback(
    async (fileId: string, type: OperationType) => {
      const cacheKey = `${fileId}_${type}`;
      let recommendWords = wordsCacheRef.current.get(cacheKey);

      // 如果缓存不存在，请求接口并更新缓存
      if (!recommendWords) {
        recommendWords = await InspirationWordsService.fetchRecommendWords(
          fileId,
          type
        );
        wordsCacheRef.current.set(cacheKey, recommendWords);
      }

      return recommendWords;
    },
    []
  );

  /**
   * 更新操作记录（去重 + 过滤过期）
   * @param prevRecords 当前记录列表
   * @param newRecord 新记录
   * @returns 更新后的记录列表（已过滤过期）
   */
  const updateRecords = useCallback(
    (
      prevRecords: OperationRecord[],
      newRecord: OperationRecord
    ): OperationRecord[] => {
      // 1. 先过滤过期记录（清理旧数据）
      const validRecords = InspirationWordsService.filterExpired(prevRecords);

      // 2. 检查是否已存在相同 fileId（不管 type 是否相同）
      const existingIndex = validRecords.findIndex(
        (r) => r.fileId === newRecord.fileId
      );

      let updatedRecords: OperationRecord[];
      if (existingIndex >= 0) {
        // 已存在相同 fileId：更新该记录的时间戳、类型和推荐词
        updatedRecords = [...validRecords];
        updatedRecords[existingIndex] = {
          ...updatedRecords[existingIndex],
          type: newRecord.type, // 更新为最新的操作类型
          timestamp: newRecord.timestamp,
          recommendWords: newRecord.recommendWords,
        };
      } else {
        // 不存在：添加新记录
        updatedRecords = [...validRecords, newRecord];
      }

      return updatedRecords;
    },
    []
  );

  /**
   * 检查并处理触发条件
   * @param records 操作记录列表
   * @param prevLastTriggerTime 上次触发时间
   * @returns 新的触发时间
   */
  const checkAndHandleTrigger = useCallback(
    (records: OperationRecord[], prevLastTriggerTime: number): number => {
      const triggerResult = InspirationWordsService.checkTrigger(
        records,
        config,
        prevLastTriggerTime
      );

      if (
        triggerResult.triggered &&
        triggerResult.fileId &&
        triggerResult.words &&
        triggerResult.triggerType &&
        triggerResult.sourceFileIds
      ) {
        const newTriggerTime = Date.now();

        // 构建 DisplayedModule 数据（包含埋点所需的元数据）
        const displayedModule: DisplayedModule = {
          words: triggerResult.words,
          triggerType: triggerResult.triggerType,
          sourceFileIds: triggerResult.sourceFileIds,
        };

        // 更新已展示的推荐词模块
        setDisplayedModules((prev) => {
          const newMap = new Map(prev);
          newMap.set(triggerResult.fileId!, displayedModule);
          return newMap;
        });

        // 调用触发回调
        onTriggered?.(triggerResult);

        return newTriggerTime;
      }

      return prevLastTriggerTime;
    },
    [config, onTriggered]
  );

  /**
   * 记录操作
   * @param fileId 文件ID（内容的唯一标识）
   * @param type 操作类型
   */
  const recordOperation = useCallback(
    async (fileId: string, type: OperationType) => {
      // AB 未开启时跳过所有逻辑
      if (!enabled) return;

      try {
        // 1. 获取推荐词（使用缓存避免重复请求）
        const recommendWords = await getRecommendWords(fileId, type);
        // 2. 新增：如果没有推荐词数据，直接返回
        if (!recommendWords || recommendWords.length === 0) {
          return;
        }

        // 2. 创建操作记录
        const newRecord: OperationRecord = {
          fileId,
          type,
          timestamp: Date.now(),
          recommendWords,
        };

        // 3. 更新记录列表（去重 + 过滤过期）
        setRecords((prevRecords) => {
          const updatedRecords = updateRecords(prevRecords, newRecord);

          // 4. 检查触发条件
          setLastTriggerTime((prevLastTriggerTime) => {
            return checkAndHandleTrigger(updatedRecords, prevLastTriggerTime);
          });

          return updatedRecords;
        });
      } catch (error) {
        console.error("[useInspirationWords] recordOperation error:", error);
      }
    },
    [enabled, getRecommendWords, updateRecords, checkAndHandleTrigger]
  );

  /**
   * 清空记录
   */
  const clearRecords = useCallback(() => {
    setRecords([]);
    setLastTriggerTime(0);
    setDisplayedModules(new Map());
  }, []);

  return {
    recordOperation,
    displayedModules,
    clearRecords,
  };
}
